import React from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

interface EngineStatusItem {
  engine_name: string;
  model_name: string;
  status: 'waiting' | 'sent' | 'received' | 'error' | 'skipped';
  round: number;
}

interface CompactEngineStatusProps {
  statuses: EngineStatusItem[];
  currentRound?: number;
  totalRounds?: number;
  totalCost?: number;
  elapsedTime?: number;
  isJudgePhase?: boolean;
}

// Engine abbreviations and colors
const engineConfig: Record<string, { abbr: string; color: string }> = {
  anthropic: { abbr: 'C', color: 'bg-orange-500' },      // Claude
  openai: { abbr: 'G', color: 'bg-green-500' },          // GPT
  google: { abbr: 'G', color: 'bg-blue-500' },           // Gemini
  perplexity: { abbr: 'P', color: 'bg-cyan-500' },       // Perplexity
  deepseek: { abbr: 'D', color: 'bg-purple-500' },       // DeepSeek
  mistral: { abbr: 'M', color: 'bg-amber-500' },         // Mistral
  xai: { abbr: 'X', color: 'bg-slate-500' },             // xAI/Grok
};

const statusStyles: Record<string, { ring: string; opacity: string }> = {
  waiting: { ring: 'ring-surface-500', opacity: 'opacity-40' },
  sent: { ring: 'ring-blue-400 ring-2', opacity: 'opacity-100' },
  received: { ring: 'ring-green-400 ring-2', opacity: 'opacity-100' },
  error: { ring: 'ring-red-400 ring-2', opacity: 'opacity-100' },
  skipped: { ring: 'ring-surface-600', opacity: 'opacity-30' },
};

export default function CompactEngineStatus({
  statuses,
  currentRound = 1,
  totalRounds = 1,
  totalCost = 0,
  elapsedTime = 0,
  isJudgePhase = false,
}: CompactEngineStatusProps) {
  if (statuses.length === 0) return null;

  // Group by engine, show only the latest round status for each engine
  const latestByEngine = new Map<string, EngineStatusItem>();
  statuses.forEach(s => {
    const existing = latestByEngine.get(s.engine_name);
    if (!existing || s.round > existing.round) {
      latestByEngine.set(s.engine_name, s);
    }
  });
  const uniqueStatuses = Array.from(latestByEngine.values());

  return (
    <div className="flex items-center gap-3 px-2 py-1.5 bg-surface-700/50 rounded-lg">
      {/* Engine icons */}
      <div className="flex items-center gap-1.5">
        {uniqueStatuses.map((status) => {
          const config = engineConfig[status.engine_name] || { abbr: status.engine_name[0].toUpperCase(), color: 'bg-surface-500' };
          const style = statusStyles[status.status];
          const isActive = status.status === 'sent';
          const isDone = status.status === 'received';
          const isError = status.status === 'error';

          return (
            <div
              key={status.engine_name}
              className={`relative w-6 h-6 rounded-full ${config.color} ${style.opacity} ${style.ring} flex items-center justify-center transition-all`}
              title={`${status.engine_name}: ${status.status}`}
            >
              {isActive ? (
                <ArrowPathIcon className="w-3.5 h-3.5 text-white animate-spin" />
              ) : isDone ? (
                <CheckCircleIcon className="w-3.5 h-3.5 text-white" />
              ) : isError ? (
                <XCircleIcon className="w-3.5 h-3.5 text-white" />
              ) : (
                <span className="text-[10px] font-bold text-white">{config.abbr}</span>
              )}
            </div>
          );
        })}

        {/* Judge indicator - shows when in judge phase */}
        {isJudgePhase && (
          <div
            className="relative w-6 h-6 rounded-full bg-yellow-500 ring-2 ring-yellow-400 flex items-center justify-center animate-pulse"
            title="Judge synthesis in progress"
          >
            <span className="text-[10px] font-bold text-white">J</span>
          </div>
        )}
      </div>

      {/* Separator */}
      <div className="w-px h-4 bg-surface-600" />

      {/* Stats */}
      <div className="flex items-center gap-2 text-xs text-surface-400">
        {isJudgePhase ? (
          <span className="text-yellow-400 font-medium">Judge</span>
        ) : totalRounds > 1 ? (
          <span>R{currentRound}/{totalRounds}</span>
        ) : null}
        <span>${totalCost.toFixed(3)}</span>
        <span>{(elapsedTime / 1000).toFixed(1)}s</span>
      </div>
    </div>
  );
}
